import React, { useCallback, useMemo } from 'react';
import type { AthleteProps, BestAvailableOrderProps, EventOrderStatProps, LeagueProps, MarketProps, MarketSideOptionProps, MatchProps, OrderProps, PollProps, TeamProps, TournamentProps, TradeProps } from "../../../types"
import MarketButton from '../MarketButton';
import { Icons, LinearDiagnal } from '../../../Components';
import { MarketButtonHelpers } from '../MarketButton/api';
import { MarketComponentHelpers } from '../../api';
import { AthleteImage } from '../../../Components/Jerseys';
import { Button, Text, View } from '../../../Components/Themed';
import { useColors } from '../../../constants/useColors';
import { MatchMarketHelpers } from './api';
import { Image } from 'react-native';

type MatchMarketProps = {
    league?:LeagueProps,
    tournament?:TournamentProps
    match:MatchProps,
    away_athlete?:AthleteProps,
    home_athlete?:AthleteProps,
    direction?:'horizontal'|'vertical',
    away_team?:TeamProps,
    hide_image?:boolean,
    disabled_markets?:string[],
    float?:boolean,
    home_team?:TeamProps,
    image_size_override?:number,
    highlight_router_orders?:boolean,
    hide_stat_banner?:boolean,
    flash_markets?:PollProps[],
    width?:number,
    style?:any,
    market:MarketProps,
    show_event?:boolean,
    hide_liquidity?:boolean,
    latest_trades:TradeProps[],
    best_available_orders:BestAvailableOrderProps[],
    event_order_stats:EventOrderStatProps[],
    default_price_view:'last_trade'|'best_available',
    onOrder:(order:OrderProps) => void,
    onFlashSelect?:(poll_id:string) => void,
    show_grades?:boolean,
    onView:(data:{event_id:string, event_type:string, market_id:string, side_type:'athlete', side_id:string}) => void,
    onTradeLongPress:(trade:TradeProps) => void
}

const MatchMarket = ({ league, match, tournament, disabled_markets, highlight_router_orders, float, flash_markets, show_event, hide_image, image_size_override, direction, style, hide_liquidity, hide_stat_banner, width, away_athlete, home_athlete, away_team, home_team, market, event_order_stats, default_price_view, latest_trades, show_grades, best_available_orders, onFlashSelect, onOrder, onView, onTradeLongPress }:MatchMarketProps) => {
    const C = useColors();
    const { liquidity, open_order_count } = useMemo(() => MarketComponentHelpers.getLiqudity(event_order_stats), [JSON.stringify(event_order_stats)])
    const is_locked = useMemo(() => MarketComponentHelpers.isMatchMarketLocked(match, market.market_id),[JSON.stringify(match.supported_markets)])
    const match_time_detail = MarketComponentHelpers.getMatchTimeDetail(match);
    const upcoming_flash_market = MatchMarketHelpers.getUpcomingFlash(flash_markets);
    let image_size = image_size_override ?? 40
    if(!image_size_override && width){ image_size = width * 0.18 }

    const getSideLabel = (side:string) => {
        if(side == 'away'){
            if(match.participant_type == 'athlete'){ return away_athlete?.last_name }
            if(match.participant_type == 'team'){ return away_team?.market_name }
        }
        if(side == 'home'){
            if(match.participant_type == 'athlete'){ return home_athlete?.last_name }
            if(match.participant_type == 'team'){ return home_team?.market_name }
        }
        return undefined
    }
    
    const renderSides = useCallback((data:{ item:MarketSideOptionProps, index:number, market:MarketProps }) => {
        const ba_order = best_available_orders.find(o => o.market_id == market.market_id && o.side == data.item.side && o.show);
        const side_details = MarketComponentHelpers.getMatchSideIds(match, data.item);
        let athlete = data.item.side == 'away' ? away_athlete : home_athlete;
        let team = data.item.side == 'away' ? away_team : home_team;
        if(!side_details.side_id || !side_details.reversed_side_id){ return <></> }
        //if(!side_details){ return <></> }
        let trade = MarketButtonHelpers.getVisibleTrade(default_price_view, data.market, data.item, latest_trades, best_available_orders, side_details.side_id, side_details.reversed_side_id); 
        if(!trade){
            trade = MarketButtonHelpers.getTradeFromMarket(market, match.match_id, 'match', data.item.side, data.item.id_source, side_details.side_id, side_details.reversed_side_id);
        }

        let side_label = getSideLabel(data.item.side);
        let disabled = false
        if(disabled_markets && disabled_markets.includes(data.market.market_id.toString())){ disabled = true }
        return (
            <View key={`${match.match_id}_${data.item.side}`} style={{ margin:2, height:50, width:75 }}>
                <MarketButton
                    side_option={data.item}
                    market={data.market}
                    side_label={side_label}
                    highlight_router_orders={highlight_router_orders}
                    trade={trade}
                    show_grades={show_grades}
                    order={ba_order}
                    locked={is_locked || disabled}
                    onLongPress={onTradeLongPress}
                    onPress={(order) => {
                        const order_title = MarketComponentHelpers.getOrderTitleForMatch(order, market, match, athlete, team);
                        if(!order_title){ return alert('Unable to process order') }
                        onOrder({ ...order, title: order_title, league_id:tournament?.league_id })
                    }}
                />
            </View>
        )
    },[away_athlete, home_athlete, is_locked, market.market_id, JSON.stringify(event_order_stats), JSON.stringify(best_available_orders), JSON.stringify(disabled_markets), default_price_view])
    if(match.participant_type == 'athlete' && (!home_athlete || !away_athlete)){ return <></> }
    if(match.participant_type == 'team' && (!home_team || !away_team)){ return <></> }
    return (
        <View float={float} style={[{ borderRadius:8 }, style]}>
            {!hide_stat_banner ?
             <LinearDiagnal 
                label={market.stat_label ?? market.type}
                label_size={14}
                style={{ borderTopLeftRadius:8, padding:5  }}
                left_color={C.text.gold}
                right_color={'#ebd197'}
            />
            :<></>}
            {show_event ?
            <View transparent style={{ padding:5 }}>
                <Text size={12} theme='h2' textAlign='center' weight='semibold'>{match.match_title}</Text>
            </View>
            :<></>}
            <View style={{ flexDirection:direction == 'horizontal' ? 'row' : 'column', flexGrow:1 }}>
                <View style={{ flexDirection:direction == 'horizontal' ? 'row' : 'column' , flexGrow:1, padding:2, justifyContent:'center', alignItems:'center' }}>
                    {!hide_image && match.participant_type == 'athlete' && away_athlete && home_athlete ?
                    
                    <View style={{ flexDirection:'row', alignItems:'center' }}>
                        <View style={{ borderRadius:100, borderWidth:match.winner == away_athlete?.athlete_id ? 2 : 0, borderColor:C.text.gold }}>
                        <AthleteImage
                            athlete={away_athlete}
                            size={image_size}
                            league={league}
                            number_size={12}
                        />
                        </View>
                        <Text style={{ marginLeft:2, marginRight:2 }} theme='h2'>VS</Text>
                        <View style={{ borderRadius:100, borderWidth:match.winner == home_athlete?.athlete_id ? 2 : 0, borderColor:C.text.gold }}>
                            <AthleteImage
                                athlete={home_athlete}
                                size={image_size} 
                                league={league}
                                number_size={12}
                            />
                        </View>
                    </View>
                    :<></>}
                    <View style={{ flexGrow:1, padding:10, paddingBottom:0 }}>
                        <Text weight='bold' size={14} style={{  maxWidth:100 }} theme='h2' textAlign='center'>{match.match_title}</Text>
                        <Text style={{ marginTop:3 }} theme='light' textAlign='center' size={12}>{match_time_detail}</Text>
                    </View>
                </View>
                <View nativeID='market_sides' style={{ flexDirection:'row', justifyContent:'flex-end', alignItems:'center', padding:10 }}>
                    {market.side_options.sort((a,b) => {
                        let a_t = a.side == market.trade_side ? 1 : 0
                        let b_t = b.side == market.trade_side ? 1 : 0
                        return a_t - b_t
                    }).map((so, index) => {
                        return renderSides({ item:so, index, market })
                    })}
                    
                </View>
            </View>
            {onFlashSelect && upcoming_flash_market ?
            <View type='footer' style={{ flexDirection:'row', alignItems:'center', padding:10, borderBottomWidth:1, borderColor:C.borders.light }}>
                {upcoming_flash_market.poll_image ?
                <Image
                    source={{ uri: upcoming_flash_market.poll_image.url }}
                    style={{ height:30, width:30, borderRadius:4 }}
                    resizeMode='cover'
                />
                :
                <Icons.FlameIcon size={24} color={C.text.error} />
                }
                <View transparent style={{ flex:1, marginLeft:10, marginRight:10 }}>
                    <Text theme='h2'>{upcoming_flash_market.poll_question}</Text>
                </View>
                <Button
                    title='BUY NOW'
                    type='success'
                    style={{ padding:10 }}
                    onPress={() => onFlashSelect(upcoming_flash_market.poll_id)}
                />
            </View>
            :<></>}
            {!hide_liquidity ?
            <View type='footer' style={{ flexDirection:'row', alignItems:'center', borderBottomLeftRadius:8, borderBottomRightRadius:8 }}>
                <Button transparent style={{ flexGrow:1, flexDirection:'row', padding:10 }} onPress={() => {
                    onView({
                        event_id: match.match_id, 
                        event_type: 'match', 
                        market_id: market.market_id, 
                        side_type: 'athlete', 
                        side_id: ''
                    });
                }}>
                    <View float style={{ height:30, width:30, borderRadius:100, justifyContent:'center', alignItems:'center' }}>
                        <Icons.LiquidityIcon color={C.text.h1} size={30}/>
                    </View>
                    <View transparent style={{ marginLeft:10 }}>
                        <Text theme='description'>Liquidity</Text>
                        <Text size={12} style={{ marginTop:3 }} color={C.text.highlight} weight='semibold'>${liquidity.toFixed()} {open_order_count} Orders</Text>
                    </View>
                </Button>
            </View>
            :<></>}
        </View>
    )
}

export default React.memo(MatchMarket, (left, right) => {
    if(JSON.stringify(left) == JSON.stringify(right)){ return true }
    return false
})